import _ from 'lodash';
import moment from 'moment-timezone';
import React from 'react';
import { TZ } from '../../../shared/consts';
import { INPUT_FMT, STATS } from '../common/constants';

const setGranularity = (daysAgo, granularity) => (Number.isFinite(daysAgo) ? 'd' : granularity);

const mapWiresWithStatsFilter = (fromDaysAgo, toDaysAgo) => {
  if (!Number.isFinite(fromDaysAgo)) {
    return w => w;
  }
  const earliest = moment.tz(TZ).startOf('day').subtract(fromDaysAgo - 1, 'days');
  const latest = toDaysAgo !== 0 ? moment.tz(TZ).startOf('day').subtract(toDaysAgo - 1, 'days') : undefined;
  return w => {
    const result = { ...w };
    STATS.forEach(key => {
      result[key] = _.get(w, key, []).filter(item => {
        const date = _.get(item, 'd');
        const count = _.get(item, 'c');
        return date && typeof count === 'number'
          && earliest.isSameOrBefore(moment.tz(date, INPUT_FMT, TZ), 'day')
          && (!latest || latest.isSameOrAfter(moment.tz(date, INPUT_FMT, TZ), 'day'));
      });
    });
    return result;
  };
};

const filterWiresWithContributors = contributors => {
  const selectedSlug = contributors.filter(c => c.selected).map(c => c.slug);
  return w => (selectedSlug.indexOf(w.contributorSlug) >= 0
    || _.intersection(w.mentionContributorSlugs, selectedSlug).length > 0
  );
};

const setWires = (rawWires, daysAgo, contributors) => {
  const list = rawWires || [];
  if (!Number.isFinite(daysAgo) && contributors.every(c => c.selected)) {
    return [...list];
  }
  return list
    .filter(filterWiresWithContributors(contributors))
    .map(mapWiresWithStatsFilter(daysAgo, 0));
};

const setPreviousPeriodWires = (rawWires, daysAgo, contributors) => {
  const list = rawWires || [];
  if (!Number.isFinite(daysAgo) && contributors.every(c => c.selected)) {
    return [];
  }
  return list
    .filter(filterWiresWithContributors(contributors))
    .map(mapWiresWithStatsFilter(daysAgo * 2, daysAgo + 1));
};

// eslint-disable-next-line complexity
const reducer = (state, { type, payload }) => {
  switch (type) {
    case 'setRequestData':
      return { ...state, requestData: payload };
    case 'setGranularity':
      return { ...state, granularity: payload };
    case 'setLoading':
      return { ...state, loading: payload };
    case 'setRawWires':
      return {
        ...state,
        rawWires: payload || [],
        wires: setWires(payload, state.daysAgo, state.contributors),
        previousPeriodWires: setPreviousPeriodWires(payload, state.daysAgo, state.contributors),
      };
    case 'setDaysAgo':
      return {
        ...state,
        daysAgo: payload,
        wires: setWires(state.rawWires, payload, state.contributors),
        previousPeriodWires: setPreviousPeriodWires(state.rawWires, payload, state.contributors),
        granularity: setGranularity(payload, state.granularity),
      };
    case 'setContributors': {
      return {
        ...state,
        contributors: payload || [],
        wires: setWires(state.rawWires, state.daysAgo, payload || []),
        previousPeriodWires: setPreviousPeriodWires(state.rawWires, state.daysAgo, payload || []),
      };
    }
    case 'setGeneratedAt':
      return { ...state, generatedAt: payload };
    default:
      throw new Error();
  }
};

const buildDefaultState = contributors => ({
  requestData: false,
  granularity: 'd',
  loading: false,
  rawWires: [],
  wires: [],
  previousPeriodWires: [],
  daysAgo: Infinity,
  generatedAt: moment(),
  contributors: (contributors || []).map(c => ({ ...c, selected: true })),
});

const DashboardContext = React.createContext();

export default DashboardContext;
export {
  buildDefaultState,
  mapWiresWithStatsFilter,
  reducer,
};
